import React from "react";
import { Box, CircularProgress, Typography } from "@mui/material";
import { toast } from "react-toastify";
import GenericFormDialog from "../GenericFormDialog";
import CustomDialog from "../CustomDialog";
import CustomButton from "../CustomButton";
import { COLORS } from "../../assets/theme";
import {
  getClassSessions,
  isScheduleConflictError,
  readScheduleConflicts,
  updateClassSchedule,
} from "../../services/ClassesService";
import { getErrorMessage } from "../../utils/apiResponse";
import { formatTableDate } from "../../utils/tableDateFormat";
import {
  ClassDefinition,
  ClassSession,
  RescheduleClassResult,
  ScheduleConflict,
} from "./types";
import {
  buildClassScheduleFields,
  buildClassScheduleInitialValues,
  buildSchedulePayloadFromValues,
  readClassFormControlState,
} from "./classFormFields";
import {
  formatTime,
  isSessionCancelled,
  sessionRowKey,
  toDateInputValue,
} from "./classUtils";
import ScheduleConflictsDialog from "./ScheduleConflictsDialog";

interface RescheduleClassDialogProps {
  open: boolean;
  classItem: ClassDefinition | null;
  onClose: () => void;
  onRescheduled: () => void;
}

/**
 * Changes when a class runs, from today forward — separate from editing its
 * name, price and capacity, which never touches the schedule.
 *
 * Future sessions nobody booked are deleted and re-projected from the new rule.
 * Sessions with people on them are left exactly where they were and come back
 * in `kept_sessions`: the class moved, those bookings did not, and nothing
 * tells those clients. That list is the whole reason this dialog has a result
 * screen instead of just a success toast.
 */
const RescheduleClassDialog: React.FC<RescheduleClassDialogProps> = ({
  open,
  classItem,
  onClose,
  onRescheduled,
}) => {
  const [submitting, setSubmitting] = React.useState(false);
  const [frequency, setFrequency] = React.useState("NONE");
  const [endMode, setEndMode] = React.useState("COUNT");
  const [result, setResult] = React.useState<RescheduleClassResult | null>(null);
  const [conflicts, setConflicts] = React.useState<ScheduleConflict[]>([]);
  const [conflictMessage, setConflictMessage] = React.useState("");
  const [conflictsOpen, setConflictsOpen] = React.useState(false);
  const [nextSession, setNextSession] = React.useState<ClassSession | null>(null);
  const [loadingSchedule, setLoadingSchedule] = React.useState(false);

  const classId = classItem?.id ? String(classItem.id) : "";

  // Reads the class's next real occurrence so the form opens showing when it
  // currently runs — right down to the duration, which is derived from the
  // session rather than trusted from the class row. Loaded before the form is
  // shown at all, so nothing typed can be overwritten when it lands.
  React.useEffect(() => {
    if (!open || !classId) return;
    let cancelled = false;

    const loadCurrentSchedule = async () => {
      setLoadingSchedule(true);
      try {
        const today = new Date();
        const until = new Date(today.getTime());
        until.setDate(until.getDate() + 60);

        const { sessions } = await getClassSessions(classId, {
          start: toDateInputValue(today),
          end: toDateInputValue(until),
        });

        const now = Date.now();
        const upcoming = sessions.find((session) => {
          if (isSessionCancelled(session.status)) return false;
          const startsAt = new Date(String(session.start_at)).getTime();
          return Number.isFinite(startsAt) && startsAt >= now;
        });
        if (!cancelled) setNextSession(upcoming ?? null);
      } catch {
        // Falls back to whatever the class row carries.
        if (!cancelled) setNextSession(null);
      } finally {
        if (!cancelled) setLoadingSchedule(false);
      }
    };

    void loadCurrentSchedule();
    return () => {
      cancelled = true;
    };
  }, [open, classId]);

  const initialValues = React.useMemo(
    () => buildClassScheduleInitialValues(classItem, nextSession),
    [classItem, nextSession]
  );

  React.useEffect(() => {
    if (!open) return;
    setResult(null);
    const { frequency: nextFrequency, endMode: nextEndMode } =
      readClassFormControlState(initialValues);
    setFrequency(nextFrequency);
    setEndMode(nextEndMode);
  }, [open, initialValues]);

  const fields = React.useMemo(
    () =>
      buildClassScheduleFields({
        frequency,
        endMode,
        startDateLabel: "New Start Date",
      }),
    [endMode, frequency]
  );

  const handleSubmit = async (values: Record<string, any>) => {
    if (!classItem) return;
    const payload = buildSchedulePayloadFromValues(values);
    if (!payload) return;

    setSubmitting(true);
    try {
      const response = await updateClassSchedule(String(classItem.id), payload);
      setResult(response);
      toast.success("Schedule updated from today forward.");
      onRescheduled();
    } catch (error) {
      // The 409 code is the signal; the itemised list may be empty, and the
      // dialog says so rather than leaving a bare toast to explain a rejection.
      const itemised = readScheduleConflicts(error);
      if (isScheduleConflictError(error)) {
        setConflicts(itemised);
        setConflictMessage(getErrorMessage(error, ""));
        setConflictsOpen(true);
        return;
      }
      toast.error(getErrorMessage(error, "Failed to change the schedule."));
    } finally {
      setSubmitting(false);
    }
  };

  const kept = result?.kept_sessions || [];
  const affected = result?.affected_enrollments || [];
  // released_sessions is authoritative when present; deleted_count is the older
  // field and only stands in when the array isn't there at all.
  const releasedCount =
    result?.released_sessions?.length ?? result?.deleted_count ?? 0;
  const nobodyAffected = kept.length === 0 && affected.length === 0;
  // Explicit false only — an older build omits the field, and "absent" must not
  // read as "nothing happened".
  const scheduleUnchanged = result?.schedule_changed === false;
  const notifiedCount = result?.clients_notified ?? affected.length;

  return (
    <>
      <CustomDialog
        open={open && loadingSchedule}
        onClose={onClose}
        title={`Reschedule — ${classItem?.name || "Class"}`}
      >
        <Box sx={{ display: "flex", justifyContent: "center", py: 4 }}>
          <CircularProgress size={28} />
        </Box>
      </CustomDialog>

      <GenericFormDialog
        open={open && !result && !loadingSchedule}
        onClose={onClose}
        title={`Reschedule — ${classItem?.name || "Class"}`}
        submitLabel="Update Schedule"
        fields={fields}
        initialValues={initialValues}
        isSubmitting={submitting}
        onValuesChange={(values) => {
          const { frequency: nextFrequency, endMode: nextEndMode } =
            readClassFormControlState(values);
          setFrequency((prev) => (prev === nextFrequency ? prev : nextFrequency));
          setEndMode((prev) => (prev === nextEndMode ? prev : nextEndMode));
        }}
        onSubmit={handleSubmit}
        additionalActions={[
          <Box
            key="hint"
            sx={{ mr: "auto", fontSize: 12, color: COLORS.typographyColor }}
          >
            {nextSession
              ? `Currently ${formatTime(nextSession.start_at)}. Sessions that already have people booked keep their old time.`
              : "Sessions that already have people booked keep their old time."}
          </Box>,
        ]}
      />

      <CustomDialog
        open={Boolean(result)}
        onClose={() => {
          setResult(null);
          onClose();
        }}
        paperSx={{ minWidth: { sm: 560 }, maxWidth: 720 }}
        title="Schedule updated"
        actions={
          <CustomButton
            label="Done"
            variant="contained"
            bgColor={COLORS.MainColor}
            textColor={COLORS.WhiteText}
            onClick={() => {
              setResult(null);
              onClose();
            }}
          />
        }
      >
        <Typography sx={{ fontSize: 14, mb: 2 }}>
          {scheduleUnchanged
            ? "Schedule unchanged — the timing you submitted matches what was already set, so nothing moved and no one was notified."
            : `${releasedCount} unbooked future session${
                releasedCount === 1 ? "" : "s"
              } were dropped and re-projected from the new schedule.`}
        </Typography>

        {scheduleUnchanged ? null : nobodyAffected ? (
          <Typography sx={{ fontSize: 14, color: "#2e7d32" }}>
            Nothing was booked ahead and nobody holds a block, so there is
            nobody to tell.
          </Typography>
        ) : (
          <>
            {/* Moved: their remaining sessions now run at the new time. On a
                class with nothing materialized yet, this is the only group
                there is. */}
            {affected.length > 0 && (
              <Box sx={{ mb: kept.length > 0 ? 2.5 : 0 }}>
                <Typography
                  sx={{ fontSize: 14, fontWeight: 700, color: "#2e7d32", mb: 0.25 }}
                >
                  {`${notifiedCount} client${
                    notifiedCount === 1 ? " was" : "s were"
                  } notified of the new time`}
                </Typography>
                <Typography sx={{ fontSize: 13, color: "#4a6b4d", mb: 1 }}>
                  Their remaining sessions moved to the new schedule and the
                  message went out automatically — there is nothing for you to
                  send.
                </Typography>
                {affected.map((entry, index) => (
                  <Box
                    key={String(entry.enrollment_id || entry.id || index)}
                    sx={{
                      border: "1px solid #d6ecd8",
                      bgcolor: "#f5fbf6",
                      borderRadius: 2,
                      p: 1.5,
                      mb: 1,
                    }}
                  >
                    <Typography sx={{ fontSize: 14, fontWeight: 600 }}>
                      {entry.full_name || "Client"}
                    </Typography>
                    <Typography sx={{ fontSize: 13, color: "#4a6b4d" }}>
                      {[
                        typeof entry.sessions_remaining === "number"
                          ? `${entry.sessions_remaining} session${
                              entry.sessions_remaining === 1 ? "" : "s"
                            } left on their block`
                          : null,
                        entry.phone,
                        entry.country,
                      ]
                        .filter(Boolean)
                        .join(" · ")}
                    </Typography>
                  </Box>
                ))}
              </Box>
            )}

            {/* Didn't move: already-booked sessions keep their old time. */}
            {kept.length > 0 && (
              <Box>
                <Typography
                  sx={{ fontSize: 14, fontWeight: 700, color: "#ed6c02", mb: 0.25 }}
                >
                  {`${kept.length} session${
                    kept.length === 1 ? "" : "s"
                  } stayed at the old time`}
                </Typography>
                <Typography sx={{ fontSize: 13, color: "#666", mb: 1 }}>
                  Already booked, so they were left exactly where they were.
                  Nothing was sent about these — tell these clients yourself.
                </Typography>
                {kept.map((session, index) => {
                  const names = (session.clients || [])
                    .map((client) => client?.full_name)
                    .filter(Boolean);
                  const count = names.length || session.client_ids?.length || 0;
                  return (
                    <Box
                      key={sessionRowKey(session, index)}
                      sx={{
                        border: "1px solid #f5e2c8",
                        bgcolor: "#fffaf3",
                        borderRadius: 2,
                        p: 1.5,
                        mb: 1,
                      }}
                    >
                      <Typography sx={{ fontSize: 14, fontWeight: 600 }}>
                        {`${formatTableDate(session.start_at)} · ${formatTime(
                          session.start_at
                        )}`}
                      </Typography>
                      <Typography sx={{ fontSize: 13, color: "#7a5320" }}>
                        {names.length > 0
                          ? names.join(", ")
                          : `${count} booked client${count === 1 ? "" : "s"}`}
                      </Typography>
                    </Box>
                  );
                })}
              </Box>
            )}

          </>
        )}
      </CustomDialog>

      <ScheduleConflictsDialog
        open={conflictsOpen}
        message={conflictMessage}
        conflicts={conflicts}
        onClose={() => setConflictsOpen(false)}
      />
    </>
  );
};

export default RescheduleClassDialog;
